/**
 * Location (direct PoI visit, S17) session state. Mirrors vendor-state.ts's
 * fetch/store pattern — REST-only, no socket, matching the backend module's
 * own precedent (location.module.ts's docstring).
 *
 * Deliberately does NOT track `gameStateStore.inventory` refresh here: a
 * search's granted items arrive via the same `inventory:updated` socket push
 * every other inventory mutation uses (InventoryService.addCarriedItems),
 * so game-state.ts's existing subscription keeps it fresh on its own.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import type {
  LocationState, LocationInstanceView, LocationNoneView, SearchResultView,
} from './location-state.types';

const INITIAL_STATE: LocationState = {
  active: null, busy: false, error: null, lastSearch: null,
};

export const locationStateStore = createStore<LocationState>({ ...INITIAL_STATE });

/** Enter a discovered Location PoI (spends poiEnergyCost server-side). */
export async function enterLocation(poiId: string): Promise<boolean> {
  locationStateStore.set((s) => ({
    ...s, busy: true, error: null, lastSearch: null,
  }));
  try {
    const instance = await api.post<LocationInstanceView>('/location/enter', { poiId });
    locationStateStore.set((s) => ({ ...s, active: instance, busy: false }));
    return true;
  } catch (err) {
    locationStateStore.set((s) => ({ ...s, busy: false, error: msg(err, "Couldn't enter — try again.") }));
    return false;
  }
}

/** Resume support — call once on mount so a page refresh mid-visit doesn't strand the player in the settlement. */
export async function loadCurrentLocation(): Promise<void> {
  try {
    const current = await api.get<LocationInstanceView | LocationNoneView>('/location/current');
    if (current.instanceId) locationStateStore.set((s) => ({ ...s, active: current as LocationInstanceView }));
  } catch {
    // best-effort — an empty settlement start is a safe fallback
  }
}

/** Search one loot object. Optimistically marks it searched so a slow response can't invite a double-click. */
export async function searchObject(objectId: string): Promise<void> {
  const { active } = locationStateStore.get();
  if (!active) return;
  const target = active.lootObjects.find((o) => o.id === objectId);
  if (!target || target.searched) return;

  locationStateStore.set((s) => (s.active ? {
    ...s,
    busy: true,
    error: null,
    active: { ...s.active, lootObjects: s.active.lootObjects.map((o) => (o.id === objectId ? { ...o, searched: true } : o)) },
  } : s));

  try {
    const result = await api.post<SearchResultView>(`/location/${active.instanceId}/search`, { objectId });
    locationStateStore.set((s) => ({ ...s, busy: false, lastSearch: result }));
  } catch (err) {
    // Roll the optimistic mark back — the server never actually recorded the search.
    locationStateStore.set((s) => (s.active ? {
      ...s,
      busy: false,
      error: msg(err, "Couldn't search that."),
      active: { ...s.active, lootObjects: s.active.lootObjects.map((o) => (o.id === objectId ? { ...o, searched: false } : o)) },
    } : s));
  }
}

/** Leaves the visit and clears local state. Best-effort — the server-side TTL covers a dropped request too. */
export async function exitLocation(): Promise<void> {
  const { active } = locationStateStore.get();
  if (!active) return;
  try {
    await api.post(`/location/${active.instanceId}/exit`, {});
  } catch {
    // ignore — Redis TTL cleans up either way
  }
  locationStateStore.set({ ...INITIAL_STATE });
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
